import {Component, OnDestroy} from '@angular/core';
import {UserInfoService} from '@app/service/user-info.service';
import {UserInfo} from '@app/model/user-info.model';
import {
	BehaviorSubject, endWith,
	filter,
	ignoreElements,
	map, merge,
	Observable,
	skipWhile,
	startWith, Subject,
	take,
	takeUntil,
	takeWhile
} from 'rxjs';
import {BreakpointObserver} from '@angular/cdk/layout';
import {shareReplay} from 'rxjs/operators';
import {ShoppingCartService} from '@app/service/shopping-cart.service';

const bootstrapBreakpoints = {
	large: '(max-width: 991px)'
};

@Component({
	selector: 'app-layout',
	templateUrl: './layout.component.html',
	styleUrls: ['./layout.component.scss']
})
export class LayoutComponent implements OnDestroy {
	userInfo$: Observable<UserInfo>;
	mobile$: Observable<boolean>;
	shoppingCartOpened$ = new BehaviorSubject<boolean>(false);
	shoppingCartToggleHighlighted$: Observable<void>;
	#destroyed$ = new Subject<void>();

	public constructor(private readonly userInfoService: UserInfoService,
										 private breakpointObserver: BreakpointObserver,
										 private shoppingCartService: ShoppingCartService) {
		this.userInfo$ = userInfoService.getUserInfo$();

		this.mobile$ = this.breakpointObserver.observe(bootstrapBreakpoints.large).pipe(
			map(result => result.matches),
			startWith(this.mobile),
			shareReplay(1)
		);

		this.autoOpenCartWhenAddingItem();
		this.shoppingCartToggleHighlighted$ = this.highlightShoppingCartToggle();
	}

	get mobile(): boolean {
		return this.breakpointObserver.isMatched(bootstrapBreakpoints.large);
	}

	ngOnDestroy(): void {
		this.#destroyed$.next();
		this.#destroyed$.complete();
	}

	private autoOpenCartWhenAddingItem(): void {
		const openedAndClosed$ = this.shoppingCartOpened$.pipe(
			skipWhile(() => !this.shoppingCartOpened$.value),
			takeWhile(() => this.shoppingCartOpened$.value),
			ignoreElements(),
			endWith(undefined)
		);

		this.shoppingCartService.articleList$.pipe(
			filter(list => list.length > 0),
			take(1),
			map(() => undefined),
			skipWhile(() => this.mobile),
			takeUntil(openedAndClosed$),
			takeUntil(this.#destroyed$)
		).subscribe(() => this.shoppingCartOpened$.next(true));
	}

	private highlightShoppingCartToggle(): Observable<void> {
		const firstItemAdded$ = this.shoppingCartService.articleList$.pipe(
			filter(list => list.length > 0),
			take(1),
			map(() => undefined)
		);

		const cartClosed$ = this.shoppingCartOpened$.pipe(
			skipWhile(() => !this.shoppingCartOpened$.value),
			takeWhile(() => this.shoppingCartOpened$.value),
			ignoreElements(),
			endWith(undefined)
		);

		return merge(firstItemAdded$, cartClosed$);
	}
}
